import { Box, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";

import { TravelCityListItem } from "./TravelCityListItem";

import { useAppSelector } from "../../app/hooks";
import { ItemCategory } from "../../constants/enum/enum";
import { TravelCities, TravelTransport } from "../../constants/travel";
import { TransportOption } from "../../types/Travel/TravelTransportOption";

import useStyles from "./index.styles";
import useCurrentTime from "../../hook/useCurrentTime";

const TravelCityList = ({
  startTravel,
  showModal,
}: {
  startTravel: (type: string, destinationCity: number) => void;
  showModal: () => void;
}) => {
  const { classes } = useStyles();
  const currentTime = useCurrentTime();

  const { myProfile, myInventoryItems, userTravelInfo } = useAppSelector(
    (state) => state.profile
  );

  const currentRegion = useMemo(
    () =>
      TravelCities.find((region) =>
        region.cities.some((city) => city.cityId === myProfile.city)
      ),
    [myProfile.city]
  );

  const hasAirplane = useMemo(
    () =>
      myInventoryItems.some(
        (item) => item.categoryId === ItemCategory.SHOPITEM && item.typeId === 9
      ),
    [myInventoryItems]
  );

  const hasCarBike = useMemo(
    () =>
      myInventoryItems.some(
        (item) =>
          item.categoryId === ItemCategory.SHOPITEM &&
          (item.typeId === 8 || item.typeId === 5)
      ),
    [myInventoryItems]
  );

  const getTransportOptions = useCallback(
    (regionName: string) => {
      const options: TransportOption[] = [];

      const isTravelling = userTravelInfo.travelUntil > currentTime;

      options.push({
        ...TravelTransport.Airplane,
        disabled: !hasAirplane || isTravelling,
      });

      if (currentRegion?.region === regionName) {
        options.push({
          ...TravelTransport.Train,
          disabled: isTravelling,
        });
        if (hasCarBike) {
          options.push({
            ...TravelTransport["Car/Motorcycle"],
            disabled: !hasCarBike || isTravelling,
          });
        }
      }

      return options;
    },
    [
      userTravelInfo.travelUntil,
      currentTime,
      hasAirplane,
      currentRegion?.region,
      hasCarBike,
    ]
  );

  return (
    <Box className={classes.travelCityListPanel}>
      <Box className={classes.travelCityListHeader}>
        <Typography>Destination</Typography>
        <Typography>Region</Typography>
        <Typography>Select available transport</Typography>
      </Box>

      <Box className={classes.travelCityList}>
        {TravelCities.map((region, index) => (
          <Box key={region.region}>
            {region.cities.map((city) => (
              <TravelCityListItem
                key={city.cityId}
                city={city}
                region={region.region}
                isCurrentCity={myProfile.city === city.cityId}
                transportOptions={getTransportOptions(region.region)}
                onTransportSelect={(type: string, cityId: number) => {
                  startTravel(type, cityId);
                  showModal();
                }}
              />
            ))}
            {index !== TravelCities.length - 1 && (
              <Box className={classes.travelCityListDivider} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TravelCityList;
